import { ComponentType, createElement, lazy, useState } from "react";

/**
 * A code-split route component that can also be rendered synchronously.
 *
 * WHY THIS EXISTS
 * ---------------
 * `renderToString` cannot wait for anything. A `React.lazy` component always
 * starts out unresolved, so on the very first render it throws its import
 * promise, the enclosing `<Suspense>` catches it, and the server serialises
 * the FALLBACK. For fly-social.com's marketing pages that fallback is
 * `<div className="min-h-screen bg-[#08080f]" />` — an empty div. That is why
 * /blogs, /comparisons/*, /industries/* and friends were shipping to Google
 * with no H1, no copy and no metadata of their own: not a metadata bug, a
 * "the page was never rendered" bug.
 *
 * It looked intermittent because `React.lazy` caches its result on the
 * component itself. Once one request has warmed a route, every later request
 * on that same Node process renders the real page. On Vercel each cold lambda
 * starts the cycle again, and these routes are served with `no-store`, so a
 * meaningful share of crawls always landed on a cold instance.
 *
 * WHAT THIS DOES
 * --------------
 * `lazyRoute` keeps the dynamic `import()` — Rollup still splits the chunk, so
 * a visitor to the landing page still never downloads /fly-app — but it also
 * keeps its own reference to the resolved module and exposes `preload()`.
 * Once the module is in memory the component renders it DIRECTLY, with no
 * lazy element and no suspension, which is what makes a single synchronous
 * `renderToString` pass emit the real page.
 *
 * The client uses the same helper (see `preloadRouteFor`), so it can await the
 * same import before `hydrateRoot`. Both sides then render the identical tree
 * and there is no hydration mismatch — the failure mode that previously forced
 * /producthunt, /redeem and /crm out of SSR entirely.
 *
 * If nothing preloads it, this behaves exactly like `React.lazy`: suspend,
 * show the fallback, swap in the page. That is the fallback path for SPA
 * navigation and for any preload that fails or times out.
 */
export interface PreloadableComponent<P> {
  (props: P): React.ReactElement | null;
  /** Loads the chunk and caches the module. Safe to call repeatedly. */
  preload: () => Promise<unknown>;
  displayName?: string;
}

export function lazyRoute<P extends object>(
  load: () => Promise<{ default: ComponentType<P> }>,
  name?: string,
): PreloadableComponent<P> {
  let loaded: { default: ComponentType<P> } | null = null;
  let inFlight: Promise<{ default: ComponentType<P> }> | null = null;

  const preload = () => {
    if (loaded) return Promise.resolve(loaded);
    if (!inFlight) {
      inFlight = load().then(
        (mod) => {
          loaded = mod;
          return mod;
        },
        (err) => {
          // Let a later attempt retry rather than caching the failure
          // forever. A chunk 404 after a deploy is the usual cause.
          inFlight = null;
          throw err;
        },
      );
    }
    return inFlight;
  };

  // React.lazy over the SAME memoised promise, so a preload already in flight
  // is reused instead of firing a second import.
  const Lazy = lazy(preload) as unknown as ComponentType<P>;

  const Route = (props: P) => {
    // Decide once per mount. Reading `loaded` on every render would flip the
    // element type from the lazy wrapper to the real component the moment the
    // chunk lands, and React treats that as a different type: it would unmount
    // the page and mount a fresh copy, losing all of its state mid-navigation.
    const [Direct] = useState<ComponentType<P> | null>(() => loaded?.default ?? null);
    return createElement(Direct ?? Lazy, props);
  };

  const Component = Route as PreloadableComponent<P>;
  Component.preload = preload;
  Component.displayName = name ? `LazyRoute(${name})` : "LazyRoute";
  return Component;
}
